
// Modern toast — Sonner/Vercel-inspired
// No left border stripe. Compact. Semantic colors on icon only.
// Near-opaque surface background with subtle tinted top border.

export const root = {
    width: '22rem',
    borderRadius: '{border.radius.lg}',
    borderWidth: '1px',
    transitionDuration: '{transition.duration}'
};

export const icon = {
    size: '1.125rem'
};

export const content = {
    padding: '0.75rem 1rem',
    gap: '0.5rem'
};

export const text = {
    gap: '0.25rem'
};

export const summary = {
    fontWeight: '500',
    fontSize: '0.8125rem'
};

export const detail = {
    fontWeight: '400',
    fontSize: '0.8125rem'
};

export const closeButton = {
    width: '1.75rem',
    height: '1.75rem',
    borderRadius: '50%',
    focusRing: {
        width: '{focus.ring.width}',
        style: '{focus.ring.style}',
        offset: '{focus.ring.offset}'
    }
};

export const closeIcon = {
    size: '0.875rem'
};

export const colorScheme = {
    light: {
        root: {
            blur: '0'
        },
        info: {
            background: '{surface.0}',
            borderColor: '{surface.border}',
            color: '{sky.600}',
            detailColor: '{surface.600}',
            shadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            closeButton: {
                hoverBackground: '{surface.100}',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem {sky.200}'
                }
            }
        },
        success: {
            background: '{surface.0}',
            borderColor: '{surface.border}',
            color: '{green.600}',
            detailColor: '{surface.600}',
            shadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            closeButton: {
                hoverBackground: '{surface.100}',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem {green.200}'
                }
            }
        },
        warn: {
            background: '{surface.0}',
            borderColor: '{surface.border}',
            color: '{amber.600}',
            detailColor: '{surface.600}',
            shadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            closeButton: {
                hoverBackground: '{surface.100}',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem {amber.200}'
                }
            }
        },
        error: {
            background: '{surface.0}',
            borderColor: '{surface.border}',
            color: '{red.600}',
            detailColor: '{surface.600}',
            shadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            closeButton: {
                hoverBackground: '{surface.100}',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem {red.200}'
                }
            }
        },
        secondary: {
            background: '{surface.0}',
            borderColor: '{surface.border}',
            color: '{surface.600}',
            detailColor: '{surface.600}',
            shadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            closeButton: {
                hoverBackground: '{surface.100}',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem {surface.200}'
                }
            }
        },
        contrast: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{surface.50}',
            detailColor: '{surface.200}',
            shadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            closeButton: {
                hoverBackground: '{surface.800}',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem {surface.400}'
                }
            }
        }
    },
    dark: {
        root: {
            blur: '0'
        },
        info: {
            background: '{surface.card}',
            borderColor: '{surface.border}',
            color: '{sky.400}',
            detailColor: 'rgba(255, 255, 255, 0.7)',
            shadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.08)',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {sky.500}, transparent 80%)'
                }
            }
        },
        success: {
            background: '{surface.card}',
            borderColor: '{surface.border}',
            color: '{green.400}',
            detailColor: 'rgba(255, 255, 255, 0.7)',
            shadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.08)',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {green.500}, transparent 80%)'
                }
            }
        },
        warn: {
            background: '{surface.card}',
            borderColor: '{surface.border}',
            color: '{amber.400}',
            detailColor: 'rgba(255, 255, 255, 0.7)',
            shadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.08)',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {amber.500}, transparent 80%)'
                }
            }
        },
        error: {
            background: '{surface.card}',
            borderColor: '{surface.border}',
            color: '{red.400}',
            detailColor: 'rgba(255, 255, 255, 0.7)',
            shadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.08)',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {red.500}, transparent 80%)'
                }
            }
        },
        secondary: {
            background: '{surface.card}',
            borderColor: '{surface.border}',
            color: '{surface.300}',
            detailColor: 'rgba(255, 255, 255, 0.7)',
            shadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.08)',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {surface.300}, transparent 80%)'
                }
            }
        },
        contrast: {
            background: '{surface.0}',
            borderColor: '{surface.100}',
            color: '{surface.950}',
            detailColor: '{surface.700}',
            shadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
            closeButton: {
                hoverBackground: '{surface.100}',
                focusRing: {
                    color: '{focus.ring.color}',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {surface.950}, transparent 80%)'
                }
            }
        }
    }
};

export default {
    root,
    icon,
    content,
    text,
    summary,
    detail,
    closeButton,
    closeIcon,
    colorScheme
};
